import { useEffect, useState } from 'react';
import { Button, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMapContext } from '@/contexts/MapContext';
import {
  BASIN_OPTIONS,
  MANAGEMENT_CATCHMENT_OPTIONS,
  OPERATIONAL_CATCHMENT_OPTIONS,
} from '@/utils/catchment';
import styles from './catchmentform.module.css';

export interface CatchmentFormValues {
  basin: string | null;
  managementCatchment: string | null;
  operationalCatchment: string | null;
}

type CatchmentFormProps = {
  isLoading: boolean;
  isInitialSelection: boolean;
  initialValues?: CatchmentFormValues | null;
  onSubmit: (values: CatchmentFormValues) => void;
};

export const CatchmentForm = ({
  isLoading,
  isInitialSelection,
  initialValues,
  onSubmit,
}: CatchmentFormProps) => {
  const { isSimulationPlaying } = useMapContext();

  const [managementOptions, setManagementOptions] = useState<string[]>([]);
  const [operationalOptions, setOperationOptions] = useState<string[]>([]);
  const [isInitialValuesSet, setIsInitialValuesSet] = useState<boolean>(false);

  useEffect(() => {
    if (!isInitialValuesSet && initialValues) {
      handleBasinChange(initialValues.basin);
      handleManagementChange(initialValues.managementCatchment);
      form.setFieldValue('operationalCatchment', initialValues.operationalCatchment);
      setIsInitialValuesSet(true);
    }
  }, [initialValues, isInitialValuesSet]);

  const form = useForm<CatchmentFormValues>({
    initialValues: initialValues ?? {
      basin: null,
      managementCatchment: null,
      operationalCatchment: null,
    },
    validate: {
      basin: (value) => (value ? null : 'A basin selection is required'),
      managementCatchment: (value) =>
        value ? null : 'A managemet catchment selection is required',
    },
  });

  const handleBasinChange = (value: string | null) => {
    form.setFieldValue('basin', value);
    form.setFieldValue('managementCatchment', null);
    form.setFieldValue('operationalCatchment', null);
    if (value && MANAGEMENT_CATCHMENT_OPTIONS[value]) {
      setManagementOptions(MANAGEMENT_CATCHMENT_OPTIONS[value]);
    } else {
      setManagementOptions([]);
    }
    setOperationOptions([]);
  };

  const handleManagementChange = (value: string | null) => {
    form.setFieldValue('managementCatchment', value);
    form.setFieldValue('operationalCatchment', null);
    if (value && OPERATIONAL_CATCHMENT_OPTIONS[value]) {
      setOperationOptions(OPERATIONAL_CATCHMENT_OPTIONS[value]);
    } else {
      setOperationOptions([]);
    }
  };

  const handleSubmit = (values: CatchmentFormValues) => {
    onSubmit(values);
    form.resetDirty();
  };

  useEffect(() => {
    if (isSimulationPlaying) {
      form.setDirty({ basin: true });
    }
  }, [isSimulationPlaying]);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form} style={{ width: '100%' }}>
      <Select
        label="River Basin District"
        placeholder="Select"
        data={BASIN_OPTIONS}
        required
        styles={{
          root: {
            width: '100%',
          },
        }}
        key={form.key('basin')}
        {...form.getInputProps('basin')}
        onChange={handleBasinChange}
        value={form.values.basin}
      />
      <Select
        label="Management Catchment"
        placeholder="Select"
        data={managementOptions}
        required
        disabled={!(managementOptions.length > 0)}
        styles={{
          root: {
            width: '100%',
          },
        }}
        key={form.key('managementCatchment')}
        {...form.getInputProps('managementCatchment')}
        onChange={handleManagementChange}
        value={form.values.managementCatchment}
      />
      <Select
        label="Operation Catchment"
        placeholder="Select"
        data={operationalOptions}
        disabled={!(operationalOptions.length > 0)}
        styles={{
          root: {
            width: '100%',
          },
        }}
        key={form.key('operationalCatchment')}
        {...form.getInputProps('operationalCatchment')}
        onChange={(value) => form.setFieldValue('operationalCatchment', value)}
        value={form.values.operationalCatchment}
      />
      <Button
        type="submit"
        variant="filled"
        loading={isLoading}
        className={styles.button}
        disabled={!form.isValid() || !form.isDirty()}
      >
        {isInitialSelection ? 'Continue' : 'Apply Change'}
      </Button>
    </form>
  );
};
